import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { ILogService, IPromise, IQService } from 'angular';
import { i18n } from 'i18next';

import { ChannelDirection, MailService, None, Option } from '@contact/upgrade';

import { IEpticaDeprecatedService } from '../eptica-deprecated.service';
import { EpticaHelper } from '../eptica.helper';
import { EpticaHelperJson } from '../eptica.helper.json';
import { EpticaData } from './eptica-data';
import { CustomerIdType, EpticaPage, IEpticaJsonResponse, IEpticaOutgoingFrame } from './eptica.request.interfaces';
import { ServiceWrapper } from './service-wrapper';

export class EpticaOutgoingPage extends EpticaPage implements IEpticaOutgoingFrame {
  private sw: ServiceWrapper;

  // ID found only after the mail is send (getId() returns a generated dummy id)
  realId: Option<number> = None;

  protected override recipientMethods = {
    to: {
      get: EpticaHelper.getOutgoingToRecipients.bind(EpticaHelper),
      set: EpticaHelper.setOutgoingToRecipients.bind(EpticaHelper),
    },
    cc: {
      get: EpticaHelper.getOutgoingCcRecipients.bind(EpticaHelper),
      set: EpticaHelper.setOutgoingCcRecipients.bind(EpticaHelper),
    },
    bcc: {
      get: EpticaHelper.getOutgoingBccRecipients.bind(EpticaHelper),
      set: EpticaHelper.setOutgoingBccRecipients.bind(EpticaHelper),
    },
  };

  constructor(
    protected override readonly frameId: string,
    private readonly idType: CustomerIdType,
    private readonly customerId: string,
    private readonly responseId: string,
    protected override readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $i18next: i18n,
    protected override readonly extApp: AppExternalService,
    protected override readonly toastDeprecatedService: ToastDeprecatedService,
    protected override readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    protected readonly mailService: MailService
  ) {
    super(frameId, extApp, toastDeprecatedService, epticaDeprecatedService, $log);
    this.sw = {
      $log: this.$log,
      $q: this.$q,
      extApp: this.extApp,
      toastDeprecatedService: this.toastDeprecatedService,
      $i18next: this.$i18next,
      sessionDeprecatedService: this.sessionDeprecatedService,
    };
  }

  changeIncomingChannelLabel() {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.changeIncomingChannelLabel(this.$i18next, frame);
    });
  }

  send(): IPromise<IEpticaJsonResponse> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        if (EpticaHelper.validateOutgoingSend(frame, this.$log)) {
          return EpticaHelperJson.sendOutgoingClick(this.sw, frame);
        } else {
          this.toastDeprecatedService.alert('agent:contact.workspace.mailOutgoing.error_form');
        }
        return this.$q.reject();
      })
      .getOrElseFunc(() => this.$q.reject());
  }

  sendPostpone(): IPromise<IEpticaJsonResponse> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        if (EpticaHelper.validateOutgoingSend(frame, this.$log)) {
          return EpticaHelperJson.sendPostponeOutgoingClick(this.sw, frame);
        } else {
          this.toastDeprecatedService.alert('agent:contact.workspace.mailOutgoing.error_form');
        }
        return this.$q.reject();
      })
      .getOrElseFunc(() => this.$q.reject());
  }

  defer(): IPromise<IEpticaJsonResponse> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        if (EpticaHelper.validateOutgoingSend(frame, this.$log)) {
          return EpticaHelperJson.saveDraftForNewClick(this.sw, frame);
        } else {
          this.toastDeprecatedService.alert('agent:contact.workspace.mailOutgoing.error_form');
        }
        return this.$q.reject();
      })
      .getOrElseFunc(() => this.$q.reject());
  }

  browseKnowledge(cancelAction: () => void, validateAction: () => void): boolean {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      // Opens the dialog
      EpticaHelper.browseKonwledgeOutgoingClick(frame);

      // Deals with cancel events
      window.setTimeout(() => EpticaHelper.overrideKnowledgeBehaviour(frame), 300);

      // Replace cancel action
      let popupContainer = frame.contentWindow!.document.getElementById('browse-kb-popup');
      if (popupContainer) {
        EpticaHelper.removeAddedButtons(popupContainer);
        EpticaHelper.overrideKnowledgeDialogCancel(popupContainer, cancelAction);
      }

      // change cancel button behaviour
      let container = frame.contentWindow!.document.getElementById('insert-answer-button');
      if (container && container.parentNode) {
        EpticaHelper.createButton(
          container.parentNode as HTMLElement,
          this.$i18next.t('agent:contact.mailactions.buttonInsertKnowledge'),
          validateAction,
          true
        );
      }
    });
    return true;
  }

  validateKnowlegdeDialog(): boolean {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        let answerButton = frame.contentWindow!.document.getElementById('insert-answer-button');
        if (answerButton && answerButton.style.display !== 'none') {
          EpticaHelper.insertKnowlegdeClick(frame);
          EpticaHelper.hideAttachmentsWithZeroSize(frame);
        } else {
          throw null;
        }
        return true;
      })
      .getOrElse(false);
  }

  cancelKnowlegdeDialog(): boolean {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        EpticaHelper.closeEpticaModal(frame, 'browse-kb-popup');
        return true;
      })
      .getOrElse(false);
  }

  getResponseId(): string {
    return this.responseId;
  }

  getId(): number {
    return this.toNumericId(this.idType, this.customerId);
  }

  getRealId(): Option<number> {
    return this.realId;
  }

  setRealId(realId: number): void {
    this.realId = Option.of(realId);
  }

  saveDraft(): IPromise<EpticaData> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        if (EpticaHelper.validateOutgoingDraft(frame, this.$log)) {
          return EpticaHelper.saveOutgoingDraftClick(
            this.$q,
            this.$i18next,
            this.$log,
            this.toastDeprecatedService,
            this.sessionDeprecatedService,
            frame
          );
        }
        return this.$q.reject();
      })
      .getOrElseFunc(() => this.$q.reject());
  }

  cancelDraft(): IPromise<EpticaData> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame =>
        EpticaHelper.cancelOutgoingDraftClick(
          this.$q,
          this.$i18next,
          this.$log,
          this.toastDeprecatedService,
          this.sessionDeprecatedService,
          frame
        )
      )
      .getOrElseFunc(() => this.$q.reject());
  }

  // Add two icons for print and preview
  customizeAnswerDisplay(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.customizeAnswerDisplay(frame, this.$i18next, this.sessionDeprecatedService, ChannelDirection.OUTGOING);
    });
  }

  // Add two icons for print and preview (for assistance requests)
  addPrintAndPreviewButtons(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.addPrintAndPreviewButtonsForOutgoing(this.$i18next, this.sessionDeprecatedService, frame);
    });
  }

  preview(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.previewOutGoing(frame);
    });
  }

  focus(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.focus(frame);
    });
  }

  print(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      // https://jira.worldline.com/browse/ODECSCNGACCEPTANCE-14970
      // EpticaHelper.print(frame);
      EpticaHelper.previewOutGoing(frame);
    });
  }

  setOutgoingClass(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.setOutgoingClass(frame);
    });
  }

  protected internalFinish(): void {
    this.epticaDeprecatedService.removeOutgoingRequest(this.getResponseId());
  }

  private toNumericId(idType: CustomerIdType, customerId: string): number {
    if (idType === 'es') {
      return parseInt(customerId, 10);
    }
    return parseInt(customerId.replace(/\D/g, ''), 10);
  }

  // hide the recipient fields
  hideRecipient(): void {
    this.doInEpticaFrame(frame => EpticaHelper.hideRecipient(frame));
  }

  override doInEpticaFrame(fn: (frame: HTMLIFrameElement) => void): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => fn(frame));
  }

  clearDropZone(): void {
    this.doInEpticaFrame(frame => EpticaHelper.clearDropZone(frame));
  }

  addUploadFiles(files: File[]): void {
    this.doInEpticaFrame(frame => EpticaHelper.addUploadFiles(frame, files, true));
  }

  addCcAndCccContacts(): void {
    this.doInEpticaFrame(frame => this.createCcAndBccContacts(frame));
  }

  protected getDirection(): ChannelDirection {
    return ChannelDirection.OUTGOING;
  }

  override preRemoveRequest(): void {
    super.preRemoveRequest();
    this.epticaDeprecatedService.removePJEventsForLoader();
  }
  preAddRequest(): void {
    this.epticaDeprecatedService.addPJEventsForLoader();
  }
}
